import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import { useAppSelector, useAppDispatch } from '@/redux/hooks'
import { fetchNegotiationHistoryAsync } from '@/redux/asyncThunks/negotiationHistory'
import { resetMessages } from '@/redux/slices/negotiationHistorySlice'
import TestChatMessages from '../settings/TestChatMessages'
import ConfirmationModal from '../OGE/OGEDeletionModal'
import { saveTestChatMessages } from '@/redux/asyncThunks/testChatWebsocket'
import { saveAIPlayerTestChatMessages } from '@/redux/asyncThunks/aiPlayerTestChatWebsocket'
import {
  updateChatBoxPosition,
  updateChatBoxDimensions,
  TEST_CHAT_ID,
  Message,
  TestMessageData,
} from '@/redux/slices/testChatSlice'
import {
  AI_PLAYER_TEST_CHAT_ID,
  updateAIPlayerChatBoxDimensions,
  updateAIPlayerChatBoxPosition,
} from '@/redux/slices/aiPlayerTestChatSlice'
import { throttle } from '@/utils/chatDialogueUtils'
import ChatBoxFooter from './ChatBox/ChatBoxFooter'
import NegotiationHistoryMessages from './ChatBox/NegotiationHistoryMessages'
import ChatBoxHeader from './ChatBox/ChatBoxHeader'
import ChatBoxResizeHandles from './ChatBox/ChatBoxResizeHandles'
import { cn } from '@/lib/utils'
import { ChatMode, TestChatType } from '@/constants/chat'

const DEFAULT_POSITION = { right: 16, bottom: 16 }

interface ChatDialogueBoxProps {
  isOpen: boolean
  onClose: () => void
  participantNames: string
  negotiationId?: number | string
  mode: ChatMode
  isReadOnly?: boolean
  chatData?: any
  chatId?: number
  initialPosition?: { right: number; bottom: number }
  testChatType?: TestChatType
}

const ChatDialogueBox: React.FC<ChatDialogueBoxProps> = ({
  isOpen,
  onClose,
  participantNames,
  negotiationId,
  mode,
  isReadOnly = false,
  chatData,
  chatId: _chatId,
  initialPosition,
  testChatType = TestChatType.Advisor,
}) => {
  const memoizedInitialPosition = useMemo(
    () => initialPosition ?? DEFAULT_POSITION,
    [initialPosition?.right, initialPosition?.bottom]
  )
  const { messages, loading, loadingMore, hasMore, currentPage, totalCount } =
    useAppSelector((state) => state.negotiationHistory)

  const testChatMessages = useAppSelector((state) =>
    testChatType === TestChatType.Advisor
      ? state.testChatMessages.messages
      : state.aiPlayerTestChatMessages.messages
  )

  const dispatch = useAppDispatch()

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const chatBoxRef = useRef<HTMLDivElement>(null)
  const animationFrameRef = useRef<number>()

  const [isResizing, setIsResizing] = useState(false)
  const [resizeDirection, setResizeDirection] = useState('')
  const [dimensions, setDimensions] = useState({ width: 448, height: 512 })
  const [position, setPosition] = useState(memoizedInitialPosition)
  const [resizeStart, setResizeStart] = useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    right: 0,
    bottom: 0,
  })

  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(true)
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const [userHasScrolled, setUserHasScrolled] = useState(false)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [previousChatMessages, setPreviousChatMessages] = useState<
    TestMessageData[]
  >([])

  useEffect(() => {
    if (chatData && chatData.messages) {
      setPreviousChatMessages(chatData.messages)
    }
  }, [chatData])

  const transformedPreviousMessages: Message[] = useMemo(() => {
    return previousChatMessages.map((msg: TestMessageData) => ({
      id: msg.id,
      message: msg.message,
      streaming: false,
      user: msg.user,
      created_at: msg.created_at,
      llm_provider: msg.llm_provider,
      llm_model_name: msg.llm_model_name,
    }))
  }, [previousChatMessages])

  useEffect(() => {
    if (
      position.right !== memoizedInitialPosition.right ||
      position.bottom !== memoizedInitialPosition.bottom
    ) {
      setPosition(memoizedInitialPosition)
    }
  }, [
    memoizedInitialPosition.right,
    memoizedInitialPosition.bottom,
    position.right,
    position.bottom,
  ])

  useEffect(() => {
    if (mode === ChatMode.TEST_CHAT || mode === ChatMode.PREVIOUS_CHAT) {
      const chatBoxId =
        mode === ChatMode.TEST_CHAT
          ? testChatType === TestChatType.Advisor
            ? TEST_CHAT_ID
            : AI_PLAYER_TEST_CHAT_ID
          : _chatId
      const isAdvisor = testChatType === TestChatType.Advisor

      if (chatBoxId !== undefined) {
        if (isAdvisor) {
          dispatch(updateChatBoxPosition({ id: chatBoxId, position }))
          dispatch(updateChatBoxDimensions({ id: chatBoxId, dimensions }))
        } else {
          dispatch(updateAIPlayerChatBoxPosition({ id: chatBoxId, position }))
          dispatch(updateAIPlayerChatBoxDimensions({ id: chatBoxId, dimensions }))
        }
      }
    }
  }, [position, dimensions, mode, _chatId, dispatch, testChatType])

  const handleMouseDown = useCallback(
    (e: React.MouseEvent, direction: string) => {
      e.preventDefault()
      e.stopPropagation()

      setIsResizing(true)
      setResizeDirection(direction)
      setResizeStart({
        x: e.clientX,
        y: e.clientY,
        width: dimensions.width,
        height: dimensions.height,
        right: position.right,
        bottom: position.bottom,
      })
    },
    [dimensions, position]
  )

  const updateDimensions = useCallback(
    (e: MouseEvent) => {
      if (!isResizing) return

      const minWidth = 300
      const minHeight = 200
      const maxWidth = window.innerWidth - 32
      const maxHeight = window.innerHeight - 32

      const deltaX = e.clientX - resizeStart.x
      const deltaY = e.clientY - resizeStart.y

      let newWidth = resizeStart.width
      let newHeight = resizeStart.height
      let newRight = resizeStart.right
      let newBottom = resizeStart.bottom

      if (resizeDirection.includes('right')) {
        newWidth = Math.min(
          maxWidth,
          Math.max(minWidth, resizeStart.width + deltaX)
        )
      }
      if (resizeDirection.includes('left')) {
        const newWidthLeft = Math.min(
          maxWidth,
          Math.max(minWidth, resizeStart.width - deltaX)
        )
        if (newWidthLeft >= minWidth) {
          newWidth = newWidthLeft
          newRight = resizeStart.right
        }
      }
      if (resizeDirection.includes('bottom')) {
        newHeight = Math.min(
          maxHeight,
          Math.max(minHeight, resizeStart.height + deltaY)
        )
      }
      if (resizeDirection.includes('top')) {
        const newHeightTop = Math.min(
          maxHeight,
          Math.max(minHeight, resizeStart.height - deltaY)
        )
        if (newHeightTop >= minHeight) {
          newHeight = newHeightTop
          newBottom = resizeStart.bottom
        }
      }

      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }

      animationFrameRef.current = requestAnimationFrame(() => {
        setDimensions({ width: newWidth, height: newHeight })
        setPosition({ right: newRight, bottom: newBottom })
      })
    },
    [isResizing, resizeDirection, resizeStart]
  )

  const throttledUpdateDimensions = useCallback(
    throttle(updateDimensions, 16), // ~60fps
    [updateDimensions]
  )

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizing) return
      throttledUpdateDimensions(e)
    },
    [isResizing, throttledUpdateDimensions]
  )

  const handleMouseUp = useCallback(() => {
    setIsResizing(false)
    setResizeDirection('')
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current)
    }
  }, [])

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove, { passive: true })
      document.addEventListener('mouseup', handleMouseUp)
      document.body.style.cursor =
        resizeDirection.includes('right') || resizeDirection.includes('left')
          ? resizeDirection.includes('top') ||
            resizeDirection.includes('bottom')
            ? resizeDirection === 'top-right' ||
              resizeDirection === 'bottom-left'
              ? 'ne-resize'
              : 'nw-resize'
            : 'ew-resize'
          : 'ns-resize'
      document.body.style.userSelect = 'none'

      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current)
        }
      }
    }
  }, [isResizing, handleMouseMove, handleMouseUp, resizeDirection])

  const loadMoreMessages = useCallback(() => {
    if (negotiationId && hasMore && !loadingMore && !loading) {
      dispatch(
        fetchNegotiationHistoryAsync({
          negotiationId,
          page: currentPage + 1,
          reset: false,
        })
      )
    }
  }, [negotiationId, hasMore, loadingMore, loading, currentPage, dispatch])

  const handleScroll = useCallback(() => {
    const container = messagesContainerRef.current
    if (!container) return

    const scrollTop = container.scrollTop
    const scrollHeight = container.scrollHeight
    const clientHeight = container.clientHeight

    if (!isInitialLoad && !userHasScrolled) {
      setUserHasScrolled(true)
    }

    if (
      !isInitialLoad &&
      userHasScrolled &&
      scrollTop <= 10 &&
      hasMore &&
      !loadingMore &&
      !loading
    ) {
      loadMoreMessages()
    }

    const atBottom = scrollHeight - scrollTop - clientHeight < 50
    setShouldScrollToBottom(atBottom)
  }, [
    hasMore,
    loadingMore,
    loading,
    loadMoreMessages,
    isInitialLoad,
    userHasScrolled,
  ])

  useEffect(() => {
    if (isOpen && negotiationId && mode === 'negotiationHistory') {
      dispatch(resetMessages())
      dispatch(
        fetchNegotiationHistoryAsync({
          negotiationId,
          page: 1,
          reset: true,
        })
      )
      setShouldScrollToBottom(true)
      setIsInitialLoad(true)
      setUserHasScrolled(false)
    }
  }, [isOpen, negotiationId, dispatch, mode])

  useEffect(() => {
    if (isInitialLoad && !loading && messages.length > 0) {
      setTimeout(() => {
        setIsInitialLoad(false)
      }, 100)
    }
  }, [isInitialLoad, loading, messages.length])

  useEffect(() => {
    if (shouldScrollToBottom && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages, shouldScrollToBottom])

  useEffect(() => {
    const container = messagesContainerRef.current
    if (container && !isInitialLoad && mode === 'negotiationHistory') {
      container.addEventListener('scroll', handleScroll)
      return () => container.removeEventListener('scroll', handleScroll)
    }
  }, [handleScroll, isInitialLoad, mode])

  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [])

  const messageCount =
    mode === ChatMode.TEST_CHAT
      ? testChatMessages.length
      : mode === ChatMode.PREVIOUS_CHAT
        ? previousChatMessages.length
        : totalCount

  const handleSave = useCallback(() => {
    if (mode === ChatMode.TEST_CHAT && testChatMessages.length > 0) {
      if (testChatType === TestChatType.Advisor) {
        dispatch(saveTestChatMessages())
      } else {
        dispatch(saveAIPlayerTestChatMessages())
      }
    }
  }, [mode, testChatMessages.length, dispatch, testChatType])

  const handleCloseRequest = useCallback(() => {
    if (
      mode === ChatMode.TEST_CHAT &&
      testChatMessages.length > 0 &&
      !isReadOnly
    ) {
      setShowConfirmDialog(true)
    } else {
      onClose()
    }
  }, [mode, testChatMessages.length, onClose, isReadOnly])

  const handleConfirmClose = useCallback(() => {
    handleSave()
    setTimeout(() => {
      onClose()
    }, 100)
  }, [handleSave, onClose])

  const handleCloseWithoutSave = useCallback(() => {
    setTimeout(() => {
      onClose()
    }, 100)
  }, [onClose])

  if (!isOpen) return null

  return (
    <div
      ref={chatBoxRef}
      className={cn(
        'fixed bg-white border border-gray-200 rounded-xl shadow-2xl z-50 flex flex-col',
        isResizing
          ? 'transition-none will-change-transform'
          : 'transition-all duration-300 ease-in-out'
      )}
      style={{
        width: `${dimensions.width}px`,
        height: `${dimensions.height}px`,
        right: `${position.right}px`,
        bottom: `${position.bottom}px`,
        transform: isResizing ? 'translateZ(0)' : undefined,
      }}
    >
      <ChatBoxResizeHandles
        isResizing={isResizing}
        resizeDirection={resizeDirection}
        onMouseDown={handleMouseDown}
      />
      <ChatBoxHeader
        participantNames={participantNames}
        messageCount={messageCount}
        onClose={handleCloseRequest}
      />

      <div className='flex-1 overflow-hidden'>
        {mode === ChatMode.TEST_CHAT ? (
          <TestChatMessages
            className='h-full'
            messages={testChatMessages}
            readOnly={isReadOnly}
            chatType={testChatType}
          />
        ) : mode === ChatMode.PREVIOUS_CHAT ? (
          <TestChatMessages
            className='h-full'
            messages={transformedPreviousMessages}
            readOnly={true}
          />
        ) : (
          <NegotiationHistoryMessages
            messagesContainerRef={messagesContainerRef}
            messagesEndRef={messagesEndRef}
            onScroll={handleScroll}
          />
        )}
      </div>

      <ChatBoxFooter mode={mode} chatData={chatData} />

      <ConfirmationModal
        isOpen={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={handleConfirmClose}
        onSecondaryAction={handleCloseWithoutSave}
        title='Save conversation?'
        description='Do you want to save the conversation before closing?'
        confirmButtonText='Save & Close'
        cancelButtonText='Cancel'
        secondaryActionText='Close without saving'
      />
    </div>
  )
}

export default ChatDialogueBox
